/** Main screen: Figma-reconciled visual layer with preserved app behavior. */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useVehicleStore } from "../store/vehicleStore"
import { useAuthStore } from "../store/authStore"
import { useTelegram } from "../hooks/useTelegram"
import { useI18n } from "../hooks/useI18n"
import { plateUi } from "../i18n/plateUi"
import { garageApi, linksApi, partnersApi, signalsApi, vehiclesApi } from "../api/endpoints"
import { ApiError } from "../api/client"
import {
  applyRussianPlateInputMask,
  isBlockedRussianPlate,
  formatPlateForDisplay,
  isValidRussianPlate,
  normalizePlateInput,
} from "../utils/plate"
import type { SignalCategory, SignalType } from "../types/api"
import { decodePromoStartParam } from "../utils/promo"
import { openSupportBot } from "../utils/supportBot"
import {
  FIGMA_MAIN_REFERENCE,
  mainVisualMode,
  RussianFlagCircle,
  SearchIcon,
  TopNotification,
  VehicleCardBlock,
} from "./MainFigmaVisuals"
import { CountrySelectionSheet } from "./CountrySelectionSheet"
import { SignalSelectionSheet } from "./SignalSelectionSheet"
import { SignalConfirmationSheet } from "./SignalConfirmationSheet"
import { AppHeader } from "./AppHeader"
import { useRubberCanvas } from "../hooks/useRubberCanvas"
import "./SearchPage.figma.css"

function isStaff(user: ReturnType<typeof useAuthStore.getState>["user"]) {
  return Boolean(
    user?.is_admin ||
      user?.is_owner ||
      user?.role === "admin" ||
      user?.role === "owner" ||
      user?.role === "moderation_admin",
  )
}

const consumedEntryKeys = new Set<string>()
const processingEntryKeys = new Set<string>()

export function SearchPage() {
  const navigate = useNavigate()
  const { vehicleId } = useParams<{ vehicleId: string }>()
  const { user } = useAuthStore()
  const {
    currentVehicle,
    signalTypes,
    searchVehicle,
    loadSuggestedVehicle,
    loadSignalTypes,
    refreshVehicle,
    shareVehicle,
    setCurrentVehicle,
    isSearching,
  } = useVehicleStore()
  const { startParam, hapticImpact, hapticNotification, shareUrl, showAlert, tg } = useTelegram()
  const { locale, t } = useI18n()

  const plateInputRef = useRef<HTMLInputElement>(null)
  const [plate, setPlate] = useState("")
  const [isPlateFocused, setIsPlateFocused] = useState(false)
  const [inputError, setInputError] = useState("")
  const [activeSheet, setActiveSheet] = useState<"none" | "country" | "signals" | "confirm">("none")
  const [selectedCategory, setSelectedCategory] = useState<SignalCategory>("road")
  const [selectedSignal, setSelectedSignal] = useState<SignalType | null>(null)
  const [allowReply, setAllowReply] = useState(false)
  const [confirmedReal, setConfirmedReal] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [notice, setNotice] = useState("")
  const [garageSignalCategory, setGarageSignalCategory] = useState<SignalCategory | null>(null)
  const [sheetError, setSheetError] = useState("")
  const [suggestedCardRequested, setSuggestedCardRequested] = useState(false)
  const [signalTargetVehicleId, setSignalTargetVehicleId] = useState<number | null>(null)
  const {
    fitAreaRef,
    scale: mainScale,
    visualWidth: mainVisualWidth,
    canvasWidth: mainCanvasWidth,
    canvasInset: mainCanvasInset,
    referenceHeight: mainReferenceHeight,
  } = useRubberCanvas(710, 16)

  const visualMode = mainVisualMode()
  const devPlatform = import.meta.env.DEV
    ? new URLSearchParams(window.location.search).get("platform")?.toLowerCase()
    : null
  const isIOS = devPlatform
    ? ["ios", "macos"].includes(devPlatform)
    : Boolean(
        visualMode?.startsWith("ios-") ||
          (!visualMode && ["ios", "macos"].includes((tg?.platform || "").toLowerCase())),
      )
  const visualVehicle = visualMode ? FIGMA_MAIN_REFERENCE : currentVehicle
  const currentUserId = user?.id
  const visualInput = Boolean(visualMode?.endsWith("input-status"))
  const visualNotice = visualMode?.endsWith("notification") ? t("main.signalSent") : ""

  useEffect(() => {
    if (!visualMode) loadSignalTypes()
  }, [loadSignalTypes, visualMode])

  useEffect(() => {
    if (visualMode || !currentUserId) {
      setGarageSignalCategory(null)
      return
    }

    let cancelled = false
    const refreshIndicator = async () => {
      try {
        const indicator = await garageApi.getSignalIndicator()
        if (!cancelled) setGarageSignalCategory(indicator.category)
      } catch {
        if (!cancelled) setGarageSignalCategory(null)
      }
    }
    const handleFocus = () => { void refreshIndicator() }
    const handleVisibility = () => {
      if (document.visibilityState === "visible") void refreshIndicator()
    }

    void refreshIndicator()
    window.addEventListener("focus", handleFocus)
    document.addEventListener("visibilitychange", handleVisibility)
    return () => {
      cancelled = true
      window.removeEventListener("focus", handleFocus)
      document.removeEventListener("visibilitychange", handleVisibility)
    }
  }, [currentUserId, visualMode])

  useEffect(() => {
    if (visualMode || !vehicleId) return
    const routeVehicleId = Number(vehicleId)
    if (!Number.isInteger(routeVehicleId) || routeVehicleId <= 0) {
      navigate("/", { replace: true })
      return
    }
    let cancelled = false
    setSignalTargetVehicleId(null)
    refreshVehicle(routeVehicleId)
      .then(() => {
        const linkedVehicle = useVehicleStore.getState().currentVehicle
        if (!cancelled && linkedVehicle?.id === routeVehicleId) {
          setPlate(linkedVehicle.plate_normalized)
          setSignalTargetVehicleId(routeVehicleId)
        }
      })
      .catch(() => {
        if (!cancelled) navigate("/", { replace: true })
      })
    return () => { cancelled = true }
  }, [vehicleId, visualMode, refreshVehicle, navigate])

  const startEntryPending = Boolean(
    startParam === "admin" ||
      (startParam && !consumedEntryKeys.has(`start:${startParam}`)),
  )

  useEffect(() => {
    if (visualMode || vehicleId || startEntryPending || currentVehicle || suggestedCardRequested) return
    setSuggestedCardRequested(true)
    loadSuggestedVehicle().catch(() => undefined)
  }, [visualMode, vehicleId, startEntryPending, currentVehicle, suggestedCardRequested, loadSuggestedVehicle])

  useEffect(() => {
    if (!notice) return
    const timer = window.setTimeout(() => setNotice(""), 3000)
    return () => window.clearTimeout(timer)
  }, [notice])

  useEffect(() => {
    if (visualMode) return
    const searchParams = new URLSearchParams(window.location.search)
    const queryPromo = searchParams.get("promo") || ""
    const queryAffiliate = searchParams.get("affiliate") || ""
    const entry = searchParams.get("entry") || ""

    if (queryPromo && entry === "garage") {
      const entryKey = `query-garage:${queryPromo}:${queryAffiliate}`
      if (consumedEntryKeys.has(entryKey)) return
      consumedEntryKeys.add(entryKey)
      navigate(`/garage-entry?promo=${encodeURIComponent(queryPromo)}&affiliate=${encodeURIComponent(queryAffiliate)}`)
      return
    }

    if (!startParam || startParam === "admin") return
    const entryKey = `start:${startParam}`
    if (consumedEntryKeys.has(entryKey) || processingEntryKeys.has(entryKey)) return

    if (startParam.startsWith("clan_")) {
      consumedEntryKeys.add(entryKey)
      navigate("/autoclan")
      return
    }

    if (startParam.startsWith("promo_") || startParam.startsWith("promo64_")) {
      const promoCode = decodePromoStartParam(startParam)
      if (!promoCode) {
        consumedEntryKeys.add(entryKey)
        return
      }

      processingEntryKeys.add(entryKey)
      partnersApi.resolveInvite(promoCode)
        .then((invite) => {
          processingEntryKeys.delete(entryKey)
          consumedEntryKeys.add(entryKey)
          navigate(`/garage-entry?promo=${encodeURIComponent(invite.promo_code)}&affiliate=${encodeURIComponent(invite.affiliate_link)}`)
        })
        .catch(() => {
          processingEntryKeys.delete(entryKey)
          showAlert(t("main.partnerInviteUnavailable"))
        })
      return
    }

    if (startParam.startsWith("signalv_")) {
      const match = /^signalv_(\d+)_(\d+)_([01])$/.exec(startParam)
      const vehicleIdFromLink = Number(match?.[1] || 0)
      const signalTypeId = Number(match?.[2] || 0)
      const replyFromLink = match?.[3] === "1"

      if (!match || !Number.isInteger(vehicleIdFromLink) || vehicleIdFromLink <= 0 || !Number.isInteger(signalTypeId) || signalTypeId <= 0) {
        consumedEntryKeys.add(entryKey)
        showAlert(t("main.signalSendError"))
        return
      }

      processingEntryKeys.add(entryKey)
      Promise.all([vehiclesApi.getCard(vehicleIdFromLink), loadSignalTypes()])
        .then(([vehicle]) => {
          const signal = useVehicleStore.getState().signalTypes.find((item) => item.id === signalTypeId)
          processingEntryKeys.delete(entryKey)
          consumedEntryKeys.add(entryKey)
          setCurrentVehicle(vehicle)
          setPlate(vehicle.plate_normalized)
          setSignalTargetVehicleId(vehicle.id)
          if (!signal) {
            showAlert(t("main.signalSendError"))
            return
          }
          setSelectedCategory(signal.category)
          setSelectedSignal(signal)
          setAllowReply(replyFromLink)
          setConfirmedReal(false)
          setSheetError("")
          setActiveSheet("confirm")
          return Promise.all([
            vehiclesApi.recordView(vehicle.id).catch(() => undefined),
            linksApi.recordClick(vehicle.id).catch(() => undefined),
          ])
        })
        .catch(() => {
          processingEntryKeys.delete(entryKey)
        })
      return
    }

    if (startParam.startsWith("vehicle_")) {
      const vehicleIdFromLink = Number(startParam.slice(8))
      if (!Number.isInteger(vehicleIdFromLink) || vehicleIdFromLink <= 0) {
        consumedEntryKeys.add(entryKey)
        return
      }

      processingEntryKeys.add(entryKey)
      vehiclesApi.getCard(vehicleIdFromLink)
        .then((vehicle) => {
          processingEntryKeys.delete(entryKey)
          consumedEntryKeys.add(entryKey)
          setCurrentVehicle(vehicle)
          setPlate(vehicle.plate_normalized)
          setSignalTargetVehicleId(vehicle.id)
          return Promise.all([
            vehiclesApi.recordView(vehicle.id).catch(() => undefined),
            linksApi.recordClick(vehicle.id).catch(() => undefined),
          ])
        })
        .catch(() => {
          processingEntryKeys.delete(entryKey)
        })
      return
    }

    // Legacy RU links remain readable, but opening them is a view, not an
    // explicit search, so they must never move last_explicit_search_at.
    if (startParam.startsWith("signal_")) {
      const match = /^signal_([A-Z0-9]+)_(\d+)_([01])$/.exec(startParam)
      const plateFromLink = applyRussianPlateInputMask(match?.[1] || "")
      const signalTypeId = Number(match?.[2] || 0)
      const replyFromLink = match?.[3] === "1"
      setPlate(plateFromLink)

      if (!match || !plateFromLink || !isValidRussianPlate(plateFromLink) || !Number.isInteger(signalTypeId) || signalTypeId <= 0) {
        consumedEntryKeys.add(entryKey)
        setInputError(plateUi(locale, isBlockedRussianPlate(plateFromLink) ? "blocked000" : "invalid"))
        return
      }

      processingEntryKeys.add(entryKey)
      Promise.all([vehiclesApi.getByPlate(plateFromLink, "RU"), loadSignalTypes()])
        .then(([vehicle]) => {
          const signal = useVehicleStore.getState().signalTypes.find((item) => item.id === signalTypeId)
          processingEntryKeys.delete(entryKey)
          consumedEntryKeys.add(entryKey)
          setCurrentVehicle(vehicle)
          setSignalTargetVehicleId(vehicle.id)
          if (!signal) {
            showAlert(t("main.signalSendError"))
            return
          }
          setSelectedCategory(signal.category)
          setSelectedSignal(signal)
          setAllowReply(replyFromLink)
          setConfirmedReal(false)
          setSheetError("")
          setActiveSheet("confirm")
          return Promise.all([
            vehiclesApi.recordView(vehicle.id).catch(() => undefined),
            linksApi.recordClick(vehicle.id).catch(() => undefined),
          ])
        })
        .catch(() => {
          processingEntryKeys.delete(entryKey)
        })
      return
    }

    if (startParam.startsWith("plate_")) {
      const plateFromLink = applyRussianPlateInputMask(startParam.slice(6))
      setPlate(plateFromLink)

      if (!plateFromLink || !isValidRussianPlate(plateFromLink)) {
        consumedEntryKeys.add(entryKey)
        setInputError(plateUi(locale, isBlockedRussianPlate(plateFromLink) ? "blocked000" : "invalid"))
        return
      }

      processingEntryKeys.add(entryKey)
      vehiclesApi.getByPlate(plateFromLink, "RU")
        .then((vehicle) => {
          processingEntryKeys.delete(entryKey)
          consumedEntryKeys.add(entryKey)
          setCurrentVehicle(vehicle)
          setSignalTargetVehicleId(vehicle.id)
          return Promise.all([
            vehiclesApi.recordView(vehicle.id).catch(() => undefined),
            linksApi.recordClick(vehicle.id).catch(() => undefined),
          ])
        })
        .catch(() => {
          processingEntryKeys.delete(entryKey)
        })
    }
  }, [visualMode, startParam, navigate, searchVehicle, loadSignalTypes, setCurrentVehicle, showAlert, t, locale])

  useEffect(() => {
    if (visualMode || startParam !== "admin" || user === null) return
    if (isStaff(user)) navigate("/admin")
  }, [visualMode, startParam, user, navigate])

  const groupedSignals = useMemo(() => {
    const groups: Record<SignalCategory, SignalType[]> = { urgent: [], road: [], attention: [] }
    signalTypes.forEach((signal) => groups[signal.category].push(signal))
    return groups
  }, [signalTypes])

  const handleSearch = useCallback(async () => {
    if (visualMode || useVehicleStore.getState().isSearching) return
    const normalized = normalizePlateInput(plate)
    if (!normalized) {
      setInputError(t("main.enterPlate"))
      return
    }
    if (isBlockedRussianPlate(normalized)) {
      setInputError(plateUi(locale, "blocked000"))
      return
    }
    if (!isValidRussianPlate(normalized)) {
      setInputError(plateUi(locale, "invalid"))
      return
    }

    setPlate(normalized)
    setInputError("")
    plateInputRef.current?.blur()
    hapticImpact("light")
    try {
      const vehicle = await searchVehicle(normalized)
      setSignalTargetVehicleId(vehicle.id)
      hapticNotification("success")
    } catch (err: any) {
      hapticNotification("error")
      setInputError(locale === "ru" && err?.message ? err.message : t("main.searchError"))
    }
  }, [visualMode, plate, searchVehicle, hapticImpact, hapticNotification, t, locale])

  const openCountry = () => {
    if (visualMode) return
    setActiveSheet("country")
  }

  const openSignals = () => {
    if (visualMode || !currentVehicle || signalTargetVehicleId !== currentVehicle.id) return
    setSelectedCategory("road")
    setSelectedSignal(null)
    setAllowReply(false)
    setConfirmedReal(false)
    setSheetError("")
    setActiveSheet("signals")
  }

  const openConfirm = () => {
    if (!selectedSignal) return
    setAllowReply(false)
    setConfirmedReal(false)
    setSheetError("")
    setActiveSheet("confirm")
  }
  const sendSelectedSignal = async () => {
    if (!currentVehicle || !selectedSignal || !confirmedReal || isSending) return
    setIsSending(true)
    setNotice("")
    setSheetError("")
    try {
      await signalsApi.send(currentVehicle.id, selectedSignal.id, allowReply)
      await refreshVehicle(currentVehicle.id).catch(() => undefined)
      setNotice(t("main.signalSent"))
      hapticNotification("success")
      setActiveSheet("none")
      setSelectedSignal(null)
      setConfirmedReal(false)
    } catch (err: any) {
      hapticNotification("error")
      const message = locale === "ru" && err instanceof ApiError ? err.detail : t("main.signalSendError")
      setSheetError(message)
    } finally {
      setIsSending(false)
    }
  }

  const handleSuggestSignal = useCallback(() => {
    openSupportBot(tg, "signal")
  }, [tg])

  const handleShare = async () => {
    if (visualMode || !currentVehicle) return
    try {
      const url = await shareVehicle(currentVehicle.id)
      shareUrl(url, `${t("main.shareCardPrefix")} ${currentVehicle.plate_display}`)
    } catch {
      showAlert(t("main.shareLinkError"))
    }
  }

  const handleGarage = async () => {
    if (visualMode) return
    try {
      const garage = await garageApi.getMine()
      if (garage.is_active && garage.is_owner && garage.vehicle_id != null) {
        navigate(`/garage/${garage.vehicle_id}`)
        return
      }
      navigate("/garage-entry")
    } catch {
      navigate("/garage-entry")
    }
  }

  const canLeaveSignal = Boolean(
    visualMode || (visualVehicle && signalTargetVehicleId === visualVehicle.id),
  )

  const closeSheet = () => {
    setActiveSheet("none")
    setSelectedSignal(null)
    setAllowReply(false)
    setConfirmedReal(false)
    setSheetError("")
  }

  return (
    <div className={`bbf-main-screen ${isIOS ? "platform-ios" : "platform-android"}`}>
      <AppHeader
        left={(isStaff(user) || Boolean(visualMode))
          ? { label: t("main.admin"), onClick: visualMode ? undefined : () => navigate("/admin") }
          : undefined}
        right={{
          label: t("main.garage"),
          onClick: handleGarage,
          className: garageSignalCategory ? `signal-${garageSignalCategory}` : undefined,
        }}
      />

      <div className="bbf-main-fit-area" ref={fitAreaRef}>
        <div
          className="bbf-main-scale-box"
          style={{
            width: `${mainVisualWidth}px`,
            height: `${mainReferenceHeight * mainScale}px`,
          }}
        >
          <div
            className="bbf-main-canvas"
            style={{
              width: `${mainCanvasWidth}px`,
              height: `${mainReferenceHeight}px`,
              transform: `scale(${mainScale})`,
              "--bbf-main-canvas-inset": `${mainCanvasInset}px`,
              "--bbf-rail-width": `${mainCanvasWidth}px`,
              "--bbf-rail-left": "0px",
              "--bbf-safe-top": "0px",
              "--bbf-bottom-space": "16px",
              "--bbf-layout-height": `${mainReferenceHeight}px`,
            } as CSSProperties}
          >
      <main className="bbf-main-content">
        <section className={`bbf-search ${visualInput || plate ? "input-state" : "recent-state"}`} aria-label={t("main.searchByPlateAria")}>
          <button
            type="button"
            className="bbf-search-country-touch"
            aria-label={t("countrySelection.openAria")}
            onClick={openCountry}
          >
            <RussianFlagCircle />
          </button>
          <input
            ref={plateInputRef}
            type="text"
            className="bbf-search-input"
            placeholder={isPlateFocused ? "" : "А 000 АА 000"}
            value={visualInput ? "А 172 МУ 888" : formatPlateForDisplay(plate)}
            onFocus={() => {
              setIsPlateFocused(true)
              if (visualMode) return
              if (currentVehicle && signalTargetVehicleId === currentVehicle.id) {
                setPlate("")
                setSignalTargetVehicleId(null)
                setInputError("")
              }
            }}
            onBlur={() => setIsPlateFocused(false)}
            onChange={(event) => {
              if (visualMode) return
              setPlate(applyRussianPlateInputMask(event.target.value))
              setSignalTargetVehicleId(null)
              setInputError("")
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleSearch()
            }}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={12}
            aria-label={t("main.plateAria")}
          />
          <button className="bbf-search-action" onClick={handleSearch} disabled={isSearching} aria-label={t("main.findPlateAria")}>
            {isSearching ? <span className="bbf-search-loading">…</span> : <SearchIcon />}
          </button>
          {inputError && <p className="bbf-search-error">{inputError}</p>}
        </section>

        {visualVehicle ? (
          <VehicleCardBlock vehicle={visualVehicle} />
        ) : (
          <section className="bbf-vehicle-card bbf-vehicle-loading" aria-live="polite">
            {t("main.loadingVehicle")}
          </section>
        )}

        <button className="bbf-main-button leave-signal" onClick={openSignals} disabled={!canLeaveSignal}>
          {t("main.leaveSignal")}
        </button>
        <button className="bbf-main-button share" onClick={handleShare} disabled={!visualVehicle}>
          {t("main.share")}
        </button>
      </main>
          </div>
        </div>
      </div>

      {(visualNotice || notice) && <TopNotification text={visualNotice || notice} />}

      {activeSheet === "country" && (
        <div className="bbf-sheet-overlay bbf-country-selection-overlay" role="presentation" onMouseDown={closeSheet}>
          <div onMouseDown={(event) => event.stopPropagation()}>
            <CountrySelectionSheet
              title={t("countrySelection.chooseCountry")}
              onBack={closeSheet}
              onNext={() => {
                setPlate("")
                setSignalTargetVehicleId(null)
                setInputError("")
                setCurrentVehicle(null)
                setSuggestedCardRequested(true)
                setActiveSheet("none")
                loadSuggestedVehicle().catch(() => undefined)
              }}
            />
          </div>
        </div>
      )}

      {activeSheet === "signals" && (
        <div className="bbf-sheet-overlay bbf-signal-selection-overlay" role="presentation" onMouseDown={closeSheet}>
          <div onMouseDown={(event) => event.stopPropagation()}>
            <SignalSelectionSheet
              selectedCategory={selectedCategory}
              signals={groupedSignals[selectedCategory]}
              selectedSignal={selectedSignal}
              onCategoryChange={(category) => {
                setSelectedCategory(category)
                setSelectedSignal(null)
                setSheetError("")
              }}
              onSignalChange={setSelectedSignal}
              onSuggestOwn={handleSuggestSignal}
              onBack={closeSheet}
              onNext={openConfirm}
            />
          </div>
        </div>
      )}

      {activeSheet === "confirm" && selectedSignal && currentVehicle && (
        <div className="bbf-sheet-overlay" role="presentation" onMouseDown={closeSheet}>
          <div onMouseDown={(event) => event.stopPropagation()}>
            <SignalConfirmationSheet
              platform={isIOS ? "ios" : "android"}
              vehicle={currentVehicle}
              selectedSignal={selectedSignal}
              allowReply={allowReply}
              confirmedReal={confirmedReal}
              isSending={isSending}
              error={sheetError}
              onAllowReplyChange={setAllowReply}
              onConfirmedRealChange={setConfirmedReal}
              onBack={() => setActiveSheet("signals")}
              onSend={sendSelectedSignal}
            />
          </div>
        </div>
      )}
    </div>
  )
}